<template>
  <div class="">
    <div
      class="grow shrink self-stretch p-5 md:px-6 md:py-5 rounded bg-racq-anti-flash-white justify-start items-start gap-4 flex flex-col md:flex-row"
    >
      <div class="grow shrink justify-start items-start gap-4 md:gap-5 flex">
        <Image :media="fields.icon" class="w-12 h-12" />
        <div class="grow shrink flex-col justify-start items-start inline-flex">
          <RichText :field="{ value: greetings }" />
        </div>
      </div>
      <FormKit
        v-if="fields.unauthenticatedButtonText?.value && !authenticated"
        type="button"
        :label="fields.unauthenticatedButtonText.value"
        inputClass="btn btn-sm btn-secondary w-full md:w-max"
        outerClass="w-full md:w-max"
        @click="loginButtonClick"
      />
      <FormKit
        v-else-if="authenticated && accountStore.prefillForm && fields.clearPrefillDetailsText?.value"
        type="button"
        :label="fields.clearPrefillDetailsText.value"
        inputClass="btn btn-sm btn-secondary w-full md:w-max"
        outerClass="w-full md:w-max"
        @click="togglePrefillDetailsButtonClick"
      />
      <FormKit
        v-else-if="authenticated && !accountStore.prefillForm && fields.restorePrefillDetailsText?.value"
        type="button"
        :label="fields.restorePrefillDetailsText.value"
        inputClass="btn btn-sm btn-secondary w-full md:w-max"
        outerClass="w-full md:w-max"
        @click="togglePrefillDetailsButtonClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAccountStore, useAnalyticsStore } from '@/store';
import { FormEvents } from '@/enums/form-events.enum';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { Field, RichText, Image } from '@sitecore-jss/sitecore-jss-vue';

const accountStore = useAccountStore();
const analyticsStore = useAnalyticsStore();
const route = useRoute();
const props = defineProps<{
  fields: Record<string, Field<any>>;
  testParams?: {
    authenticated: boolean;
    firstName?: string;
  };
}>();

const authenticated = ref(false);

// props.testParams is only populated via storybook to set initial state
if (props.testParams) {
  authenticated.value = props.testParams.authenticated;
  accountStore.prefillForm = props.testParams.authenticated;
  accountStore.setIdentityDetailsFirstName(props.testParams.firstName ?? '');
}

const greetings = computed(() => {
  if (authenticated.value) {
    const field = !accountStore.prefillForm
      ? (props.fields?.authenticatedGreetingsRestore?.value ?? '')
      : (props.fields?.authenticatedGreetings?.value ?? '');
    return replaceBookmark('firstName', field, accountStore.identityDetails?.firstName ?? '');
  } else {
    return props.fields?.unauthenticatedGreetings?.value;
  }
});

// Lifecycle hooks
// ------------------------------------------------------------- //
onMounted(() => {
  populateAccountLoginInfoBox();
});

const setUrlParam = () => {
  const url = new URL(window.location.href);
  url.searchParams.set('login', 'true');
  window.history.pushState(null, '', url.toString());
};

const loginButtonClick = async () => {
  if (!authenticated.value) {
    analyticsStore.pushAnalytics({
      event: FormEvents.LOGIN_START,
      form: {
        name: 'Login',
        stepName: 'Start',
      },
    });
    setUrlParam();
    await accountStore.authenticate(route.fullPath, false, true);
  }
};

const togglePrefillDetailsButtonClick = () => {
  accountStore.togglePrefillDetails();
};

const populateAccountLoginInfoBox = async () => {
  try {
    if (!props.testParams) {
      accountStore.resetAuthCheckStatus();
      const loggedIn = await accountStore.isAuthenticated;
      if (loggedIn) {
        await accountStore.fetchIdentityDetails();
      }
      authenticated.value = loggedIn || accountStore.partyMatched;
    }
  } catch (error) {
    console.error(error);
  }
};

defineExpose({ populateAccountLoginInfoBox });
</script>
